import type { OrganizationWorkflowPayload } from '@Application/Organizations/Workflows/Actions/organizationWorkflowsFetched';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import WorkflowPreparationSettingsView from '@UI/Store/OrganizationWorkflows/Views/WorkflowPreparationSettingsView';
import WorkflowStageView from '@UI/Store/OrganizationWorkflows/Views/WorkflowStageView';
import WorkflowStepView from '@UI/Store/OrganizationWorkflows/Views/WorkflowStepView';

export type OrganizationWorkflowArgs = {
	readonly key: string;
	readonly version: number;
	readonly publishedAt: string;
	readonly label: string;
	readonly description: string | null;
	readonly launchMode: LabeledValueView;
	readonly isActive: boolean;
	readonly preparation: WorkflowPreparationSettingsView | null;
	readonly steps: readonly WorkflowStepView[];
	readonly separatesSpeakers: boolean;
};

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * The current version of one workflow of an organization: a document group (`proposal`) or the
 * automatic transcription (`automatic`). `key` is stable across versions. `preparation` is null for
 * the automatic transcription.
 */
class OrganizationWorkflowView {
	readonly key: string;
	readonly version: number;
	readonly publishedAt: string;
	readonly label: string;
	readonly description: string | null;
	readonly launchMode: LabeledValueView;
	readonly isActive: boolean;
	readonly preparation: WorkflowPreparationSettingsView | null;
	readonly steps: readonly WorkflowStepView[];
	readonly separatesSpeakers: boolean;

	constructor(args: OrganizationWorkflowArgs) {
		this.key = args.key;
		this.version = args.version;
		this.publishedAt = args.publishedAt;
		this.label = args.label;
		this.description = args.description;
		this.launchMode = args.launchMode;
		this.isActive = args.isActive;
		this.preparation = args.preparation;
		this.steps = args.steps;
		this.separatesSpeakers = args.separatesSpeakers;

		Object.freeze(this);
	}

	static fromPayload(payload: OrganizationWorkflowPayload): OrganizationWorkflowView {
		return new OrganizationWorkflowView({
			key: payload.key,
			version: payload.version,
			publishedAt: payload.publishedAt,
			label: payload.label,
			description: payload.description,
			launchMode: new LabeledValueView(payload.launchMode),
			isActive: payload.isActive,
			preparation:
				payload.preparation !== null
					? new WorkflowPreparationSettingsView(payload.preparation)
					: null,
			steps: payload.steps.map((step) => new WorkflowStepView(step)),
			separatesSpeakers: payload.separatesSpeakers,
		});
	}

	get isAutomatic(): boolean {
		return this.launchMode.value === 'automatic';
	}

	/** Whether the chain has a speaker separation step, the only case where `separatesSpeakers` applies. */
	get hasSpeakerSeparationStep(): boolean {
		return this.steps.some((step) => step.type.value === 'speaker_separation');
	}

	/** The documents grouped by stage, in stage order then position order. */
	get stages(): readonly WorkflowStageView[] {
		const stageNumbers = [...new Set(this.steps.map((step) => step.stage))].sort(
			(first, second) => first - second,
		);

		return stageNumbers.map(
			(stage) =>
				new WorkflowStageView(
					stage,
					this.steps
						.filter((step) => step.stage === stage)
						.sort((first, second) => first.position - second.position),
				),
		);
	}

	withActive(isActive: boolean): OrganizationWorkflowView {
		return new OrganizationWorkflowView({ ...this, isActive });
	}
}

export default OrganizationWorkflowView;
